import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { FormControl, IconButton, MenuItem, Select, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { shadowHideUser } from '../../api/user';
import { toast } from 'react-toastify'
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";


const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: 4,
    maxHeight: '80vh',
    overflow: 'auto',
};
const reasons = [
    "Content Violation/Invalid Inputs",
    "Personal Detail Exposure",
    "Inappropriate profile",
    "Message Spams",
    "Other"
]

// Shadow hide (women only): removes her from Members search + search count and
// silently drops her outbound message / profile-view / favourite notifications.
// Her own app experience stays normal. Independent of the regular "Hide from
// Search" (isInvisible) action.
const ShadowHideModal = ({ open, onClose, user, onUpdated }) => {
    const [updating, setUpdating] = useState(false);
    const [reason, setReason] = useState("")
    const [customReason, setCustomReason] = useState("")

    const isHidden = Boolean(user?.shadowHiddenByAdmin);

    const handleConfirm = async () => {
        if (!user?._id) return;
        try {
            setUpdating(true);
            const modifyReason = reason !== "Other" ? reason : customReason
            if (!isHidden) {
                if (!modifyReason) {
                    toast.error('Please mention the reason.');
                    return
                }
                if (reason === "Other" && modifyReason?.length > 200) {
                    toast.error('The other reason must be within 200 characters.');
                    return
                }
            }
            const res = await shadowHideUser({ userId: user._id, hideReason: modifyReason });
            if (res?.success !== false) {
                onClose?.();
                onUpdated?.();
                toast.success('Profile search visibility updated successfully');
                setReason('')
                setCustomReason('')
            } else {
                toast.error(res?.error || 'Could not update profile visibility');
            }
        } catch (err) {
            console.error('Error while updating shadow hide', err);
            toast.error('Could not update profile visibility');
        } finally {
            setUpdating(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="shadow-hide-modal-title"
            aria-describedby="shadow-hide-modal-description"
        >
            <Box sx={style}>
                <IconButton
                    sx={{ position: 'absolute', top: 10, right: 10 }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
                <div>
                    {isHidden ? (
                        <>
                            <h5 className="font-36">Restore to Search</h5>
                            <p>Make this profile appear in Members search again and resume delivery of her messages, profile views and favourites.</p>
                        </>
                    ) : (
                        <div>
                            <h5 className="font-36">Shadow Hide (Women)</h5>
                            <p>Remove this profile from Members search and the search count. Her own app stays normal — she can browse, view, favourite and message — but her messages, profile views and favourites will not be delivered to or notify other members.</p>
                            <FormControl
                                size="small"
                                sx={{
                                    marginBottom: 2,
                                    width: "100%",
                                    boxShadow: 'none',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        boxShadow: 'none',
                                        '& fieldset': {
                                            borderColor: '#d1d5db',
                                            borderWidth: '1px',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2563eb',
                                            borderWidth: '1.5px',
                                        },
                                    },
                                }}
                            >
                                <Select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    displayEmpty
                                    renderValue={(selectedValue) => {
                                        if (selectedValue === '') {
                                            return <Typography color="textSecondary">Select a reason</Typography>;
                                        }
                                        return selectedValue;
                                    }}
                                    IconComponent={KeyboardArrowDownIcon}
                                    sx={{
                                        borderRadius: "8px",
                                        fontSize: "14px",
                                        "& .MuiSelect-select": { padding: "10px 14px" },
                                        "& .MuiSvgIcon-root": { color: "#747474ff", fontSize: "22px" },
                                    }}
                                >
                                    {reasons?.map(item => (
                                        <MenuItem key={item} value={item} sx={{ fontSize: "14px", fontWeight: 500 }}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {reason === "Other" && <div className="mb-16">
                                <label className="mb-8" style={{ fontSize: "14px", fontWeight: 500 }}>
                                    Other Reason
                                </label>
                                <textarea
                                    className="form-control"
                                    placeholder="Write other reason..."
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                ></textarea>
                            </div>}
                        </div>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                        <button
                            className="badge text-md fw-medium bg-warning-900 px-20 py-9 radius-8 text-white"
                            onClick={handleConfirm}
                            disabled={updating || (!isHidden && !reason)}
                            aria-busy={updating}
                        >
                            {updating ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={18} color="inherit" />
                                    Updating...
                                </span>
                            ) : (
                                'Confirm'
                            )}
                        </button>
                    </Box>
                </div>
            </Box>
        </Modal>
    );
};

export default ShadowHideModal;
